import { defineStore } from "pinia";
import { useGeneralStore } from "%/stores/general-store";
import { links } from "%/enums/link-enums";
import { $axios } from "@bloglovin/vue-framework";
import SmartFeedEngine from "SmartFeed/smart-feed-engine";
import { useCompanyUsersFeedStore } from "SmartFeed/stores/feed/company-users-feed-store";

export const useOrganizationStore = defineStore("organization-store", {
  state: () => ({
    isLoading: false,
    isLoadingUpdateOrgName: false,
    isLoadingUpdateOrgDescription: false,
    loadingUsers: false,
    loadingOrgServiceOffered: false,
    loadingPublicOrgs: false,
    loadingAdvertiserOrgs: false,
    loadingCreatorOrgs: false,
    loadingCreateUser: false,
    loadingUpdateOrgVisibility: false,
    serviceOfferedError: "",
    editOrgName: "",
    editOrgNameError: "",
    users: [],
    publicOrgs: [],
    advertiserOrgs: [],
    creatorOrgs: [],
    errorMessage: "",
    lastUserId: 0,
    lastPublicOrgId: 0,
    lastAdvertiserOrgId: 0,
    lastCreatorOrgId: 0,
    signupErrorMessage: "",
    isAddUserModalVisible: false,
    hasMorePublicOrgs: false,
    hasMoreAdvertiserOrgs: false,
    hasMoreCreatorOrgs: false,
    addUserErrorMessage: "",
    activeUsers: [],
    orgProfileData: [],
    assertOrgExists: [],
    uploadLogoError: "",
    uploadSplashError: "",
    isUploadingLogo: false,
    isUploadingSplash: false,
    isChangeRoleModalOpen: false,
    changeRoleErrorMessage: "",
    changeRoleSelectedOption: {},
    activeChangeRoleUserId: null,
  }),
  getters: {
    usersApiV1Url() {
      return useGeneralStore().getLink(links.usersApiV1);
    },
    getOrgData() {
      return useGeneralStore().orgData;
    },
    orgName() {
      return this.getOrgData.name;
    },
    orgDescription() {
      return this.getOrgData.org_description;
    },
    orgNameNotEdited() {
      return this.orgName === this.editOrgName;
    },
    userId() {
      return useGeneralStore().userId;
    },
    hasMoreUsers() {
      return this.lastUserId !== -1;
    },
    getUserIndexById: state => id => {
      return state.users.findIndex(el => el.user_id === id);
    },
    hasPublicationCapability() {
      return this.orgProfileData.capabilities.includes(3);
    },
    hasAdvertiserCapability() {
      return this.orgProfileData.capabilities.includes(2) || this.orgProfileData.capabilities.includes(1);
    },
    hasClaimedAdvertiserCapability() {
      return this.orgProfileData.capabilities.includes(2);
    },
  },
  actions: {
    revokeUserInvitation(userId) {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      this.apiPost(
        `${this.usersApiV1Url}/org/${this.getOrgData.org_id}/user/${userId}/invitation/revoke`,
        {},
        response => {
          new SmartFeedEngine().addEntities(response.entities);
        },
        error => {
          this.errorMessage =
            error.response?.data?.message || "Failed to revoke user invitation.";
        },
        () => {
          this.isLoading = false;
        },
      );
    },

    reactivateUserInvitation(userId) {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      this.apiPost(
        `${this.usersApiV1Url}/org/${this.getOrgData.org_id}/user/${userId}/invitation/reactivate`,
        {},
        response => {
          new SmartFeedEngine().addEntities(response.entities);
        },
        error => {
          this.errorMessage =
            error.response?.data?.message || "Failed to reactivate user invitation.";
        },
        () => {
          this.isLoading = false;
        },
      );
    },

    approveUser(userId) {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      this.apiPost(
        `${this.usersApiV1Url}/org/${this.getOrgData.org_id}/user/${userId}/approve`,
        {},
        response => {
          new SmartFeedEngine().addEntities(response.entities);
        },
        error => {
          this.errorMessage =
            error.response?.data?.message || "Failed to approve user.";
        },
        () => {
          this.isLoading = false;
        },
      );
    },

    rejectUser(userId) {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      this.apiPost(
        `${this.usersApiV1Url}/org/${this.getOrgData.org_id}/user/${userId}/reject`,
        {},
        response => {
          new SmartFeedEngine().addEntities(response.entities);
        },
        error => {
          this.errorMessage =
            error.response?.data?.message || "Failed to reject user.";
        },
        () => {
          this.isLoading = false;
        },
      );
    },

    reactivateUserApplication(userId) {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      this.apiPost(
        `${this.usersApiV1Url}/org/${this.getOrgData.org_id}/user/${userId}/application/reactivate`,
        {},
        response => {
          new SmartFeedEngine().addEntities(response.entities);
        },
        error => {
          this.errorMessage =
            error.response?.data?.message || "Failed to reactivate user application.";
        },
        () => {
          this.isLoading = false;
        },
      );
    },

    uploadSplash(file) {
      this.isUploadingSplash = true;
      let formData = new FormData();
      formData.append("splash", file);
      $axios.post(`/org/${this.getOrgData.org_id}/splash/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }).then(response => {
        useGeneralStore().$state.orgData = {
          ...this.getOrgData,
          splash_url: response.data.url,
        };
      }).catch(() => {
        this.uploadSplashError = "Error uploading splash image";
      }).finally(() => {
        this.isUploadingSplash = false;
      });
    },
    uploadLogo(file) {
      this.isUploadingLogo = true;
      let formData = new FormData();
      formData.append("logo", file);
      $axios.post(`/org/${this.getOrgData.org_id}/logo/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }).then(response => {
        useGeneralStore().$state.orgData = {
          ...this.getOrgData,
          logo_url: response.data.url,
        };
      }).catch(() => {
        this.uploadLogoError = "Error uploading logo image";
      }).finally(() => {
        this.isUploadingLogo = false;
      });
    },
    updateOrgName(orgName) {
      if (this.isLoadingUpdateOrgName) {
        return;
      }

      this.isLoadingUpdateOrgName = true;
      this.apiPost(
        `${this.usersApiV1Url}/org/${this.getOrgData.org_id}/rename`, {
          user_id: this.userId,
          org_name: orgName,
        }, () => {
          useGeneralStore().setOrgName(orgName);
        }, () => {

        }, () => {
          this.isLoadingUpdateOrgName = false;
        },
      );
    },

    fetchOrgUsers() {
      if (this.lastUserId === -1 || this.loadingUsers) {
        return;
      }
      this.loadingUsers = true;
      this.apiGet(
        `${this.usersApiV1Url}/org/${this.getOrgData.org_id}/users`,
        {
          last_id: this.lastUserId,
        },
        response => {
          const newUsers = response.users.map(user => ({
            user_id: user.user_id,
            email_address: user.email_address,
            name: `${user.first_name} ${user.last_name}`,
            role_id: user.role_id,
            status: user.status,
          }));
          this.users = [...this.users, ...newUsers];
          this.lastUserId = newUsers.length ? newUsers.at(-1).user_id : -1;
          this.loadingUsers = false;
        },
      );
    },
    fetchActiveOrgUsers(orgId) {
      if (this.lastUserId === -1 || this.loadingUsers) {
        return;
      }
      this.loadingUsers = true;
      this.apiGet(
        `/org/${orgId}/users/active-with-avatars`,
        {
          last_id: this.lastUserId,
        },
        response => {
          const newUsers = response.users.map(user => ({
            user_id: user.user_id,
            email_address: user.email_address,
            name: `${user.first_name} ${user.last_name}`,
            role_id: user.role_id,
            status: user.status,
            avatar_url: user.avatar_url ?? "",
          }));

          this.users = [...this.users, ...newUsers];
          this.activeUsers = this.users;
          this.lastUserId = newUsers.length ? newUsers.at(-1).user_id : -1;

          this.loadingUsers = false;
        },
        () => {
          this.errorMessage = "Error fetching active org users";
        },
      );
    },
    fetchPublicOrgs() {
      if (!this.hasMorePublicOrgs || this.loadingPublicOrgs) {
        return;
      }

      this.loadingPublicOrgs = true;
      this.apiGet(
        "/org/orgs/public",
        {
          last_id: this.lastPublicOrgId,
        },
        data => {
          this.publicOrgs = [...this.publicOrgs, ...data.orgs];
          this.lastPublicOrgId = data.lastId;
          this.hasMorePublicOrgs = data.hasMore;
        },
        () => {},
        () => {
          this.loadingPublicOrgs = false;
        },
      );
      this.loadingPublicOrgs = false;
    },
    fetchAdvertiserOrgs() {
      if (!this.hasMoreAdvertiserOrgs || this.loadingAdvertiserOrgs) {
        return;
      }

      this.loadingAdvertiserOrgs = true;
      this.apiGet(
        "/org/orgs/advertisers",
        {
          last_id: this.lastAdvertiserOrgId,
        },
        data => {
          this.advertiserOrgs = [...this.advertiserOrgs, ...data.orgs];
          this.lastAdvertiserOrgId = data.lastId;
          this.hasMoreAdvertiserOrgs = data.hasMore;
        },
        () => {},
        () => {
          this.loadingAdvertiserOrgs = false;
        },
      );
      this.loadingAdvertiserOrgs = false;
    },
    fetchCreatorOrgs() {
      if (!this.hasMoreCreatorOrgs || this.loadingCreatorOrgs) {
        return;
      }

      this.loadingCreatorOrgs = true;
      this.apiGet(
        "/org/orgs/public",
        {
          last_id: this.lastCreatorOrgId,
        },
        data => {
          this.creatorOrgs = [...this.creatorOrgs, ...data.orgs];
          this.lastCreatorOrgId = data.lastId;
          this.hasMoreCreatorOrgs = data.hasMore;
        },
        () => {},
        () => {
          this.loadingCreatorOrgs = false;
        },
      );
      this.loadingCreatorOrgs = false;
    },
    async assertOrgExistsByEmail(email) {
      await this.apiGet(
        "/org/exists-by-email",
        {
          email: email,
        },
        response => {
          this.assertOrgExists = response;
        },
      );
    },
    async createUserInOrg(email, roleId, firstName, lastName) {
      this.loadingCreateUser = true;
      this.addUserErrorMessage = "";

      await this.apiPost(
        "/user/invite/org",
        {
          org_id: this.getOrgData.org_id,
          email: email,
          role_id: roleId,
          first_name: firstName,
          last_name: lastName,
        },
        () => {
          useCompanyUsersFeedStore().reload();
          this.closeAddUserModal();
          this.loadingCreateUser = false;
        },
        error => {
          this.addUserErrorMessage = error.response.data.message;
          this.loadingCreateUser = false;
        },
      );
    },
    updateOrgProfileVisibility(visibility) {
      this.loadingUpdateOrgVisibility = true;
      this.apiPost(
        `/org/${this.getOrgData.org_id}/profile/visibility/update`,
        {
          "profile_visibility": visibility,
        },
        () => {
          this.loadingUpdateOrgVisibility = false;
        },
        () => {
          this.loadingUpdateOrgVisibility = false;
        },
      );
    },
    updateOrgDescription(orgDescription) {
      this.isLoadingUpdateOrgDescription = true;
      this.apiPost(
        `/org/${this.getOrgData.org_id}/profile/description/update`, {
          "org_description": orgDescription,
        }, () => {
          useGeneralStore().$state.orgData = {
            ...this.getOrgData,
            org_description: orgDescription,
          };
        }, () => {

        }, () => {
          this.isLoadingUpdateOrgDescription = false;
        },
      );
    },
    disableUser(userId) {
      this.isLoading = true;
      this.apiPost(
        `${this.usersApiV1Url}/org/${this.getOrgData.org_id}/user/${userId}/disable`,
        {},
        response => {
          new SmartFeedEngine().addEntities(response.entities);
        },
        error => {
          this.signupErrorMessage =
                        error.message || "Unable to disable user";
        },
      );
    },
    enableUser(userId) {
      this.apiPost(
        `${this.usersApiV1Url}/org/${this.getOrgData.org_id}/user/${userId}/enable`,
        {},
        response => {
          new SmartFeedEngine().addEntities(response.entities);
        },
        error => {
          this.signupErrorMessage =
                        error.message || "Unable to enable user";
        },
      );
    },

    changeUserRole(userId, orgId, roleId, newRoleId) {
      if (roleId === +newRoleId) {
        this.changeRoleErrorMessage = "User already has this role selected";
        return;
      }
      this.apiPost(
        `/org/${orgId}/user/${userId}/role/change`,
        {
          role_id: roleId,
          new_role_id: newRoleId,
        },
        response => {
          new SmartFeedEngine().addEntities(response.entities);
          this.closeChangeRoleModal();
        },
        error => {
          this.signupErrorMessage =
                        error.message || "Something unexpected went wrong during role change";
        },
      );
    },

    addServiceToOrg(serviceId) {
      this.loadingOrgServiceOffered = true;
      this.apiPost(
        `/org/${this.getOrgData.org_id}/service/add`,
        {
          service_id: serviceId,
        },
        () => {
          this.updateServiceStatus(serviceId, "active");
          this.loadingOrgServiceOffered = false;
        },
        () => {
          this.serviceOfferedError = "Unable to update offered service";
          this.loadingOrgServiceOffered = false;
        },
      );
    },

    removeServiceFromOrg(serviceId) {
      this.loadingOrgServiceOffered = true;
      this.apiPost(
        `/org/${this.getOrgData.org_id}/service/remove`,
        {
          service_id: serviceId,
        },
        () => {
          this.updateServiceStatus(serviceId, "inactive");
          this.loadingOrgServiceOffered = false;
        },
        () => {
          this.serviceOfferedError = "Unable to update offered service";
          this.loadingOrgServiceOffered = false;
        },
      );
    },

    updateServiceStatus(serviceId, status) {
      const serviceKey = Object.keys(this.getOrgData.services_offered).find(key => {
        return this.getOrgData.services_offered[key].service_id === serviceId;
      });

      if (serviceKey) {
        this.getOrgData.services_offered[serviceKey].status = status;
      }
    },

    updateUserStatus(userId, status) {
      const userIndex = this.getUserIndexById(userId);
      if (userIndex !== -1) {
        this.users[userIndex].status = status;
      }
    },

    updateUserRole(userId, roleId) {
      const userIndex = this.getUserIndexById(userId);
      if (userIndex !== -1) {
        this.users[userIndex].role_id = roleId;
      }
    },
    closeAddUserModal() {
      this.addUserErrorMessage = "";
      this.isAddUserModalVisible = false;
    },
    openAddUserModal() {
      this.addUserErrorMessage = "";
      this.isAddUserModalVisible = true;
    },
    openChangeRoleModal(userId) {
      this.changeRoleErrorMessage = "";
      this.changeRoleSelectedOption = {};
      this.isChangeRoleModalOpen = true;
      this.activeChangeRoleUserId = userId;
    },
    closeChangeRoleModal() {
      this.isChangeRoleModalOpen = false;
      this.changeRoleErrorMessage = "";
      this.changeRoleSelectedOption = {};
      this.activeChangeRoleUserId = null;
    },
    setChangeRoleSelectedOption(newValue) {
      this.changeRoleSelectedOption = newValue;
    },

    revokeOrgUserInvitation(orgId, userId) {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      this.apiPost(
        `/org/${orgId}/user/${userId}/invitation/revoke`,
        {},
        response => {
          new SmartFeedEngine().addEntities(response.entities);
        },
        error => {
          this.errorMessage =
            error.response?.data?.message || "Failed to revoke organization user invitation.";
        },
        () => {
          this.isLoading = false;
        },
      );
    },

    reactivateOrgUserInvitation(orgId, userId) {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      this.apiPost(
        `/org/${orgId}/user/${userId}/invitation/reactivate`,
        {},
        response => {
          new SmartFeedEngine().addEntities(response.entities);
        },
        error => {
          this.errorMessage =
            error.response?.data?.message || "Failed to reactivate organization user invitation.";
        },
        () => {
          this.isLoading = false;
        },
      );
    },

    approveOrgUser(orgId, userId) {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      this.apiPost(
        `/org/${orgId}/user/${userId}/approve`,
        {},
        response => {
          new SmartFeedEngine().addEntities(response.entities);
        },
        error => {
          this.errorMessage =
            error.response?.data?.message || "Failed to approve organization user.";
        },
        () => {
          this.isLoading = false;
        },
      );
    },

    rejectOrgUser(orgId, userId) {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      this.apiPost(
        `/org/${orgId}/user/${userId}/reject`,
        {},
        response => {
          new SmartFeedEngine().addEntities(response.entities);
        },
        error => {
          this.errorMessage =
            error.response?.data?.message || "Failed to reject organization user.";
        },
        () => {
          this.isLoading = false;
        },
      );
    },

    deactivateOrgUser(orgId, userId) {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      this.apiPost(
        `/org/${orgId}/user/${userId}/deactivate`,
        {},
        response => {
          new SmartFeedEngine().addEntities(response.entities);
        },
        error => {
          this.errorMessage =
            error.response?.data?.message || "Failed to deactivate organization user.";
        },
        () => {
          this.isLoading = false;
        },
      );
    },

    resendInviteUserToOrgMail(userId, orgId, email, firstName, lastName) {
      if (this.isLoading) {
        return;
      }
      this.isLoading = true;
      this.apiPost(
        `/user/${userId}/invite/org/resend`,
        {
          org_id: orgId,
          email: email,
          first_name: firstName,
          last_name: lastName,
        },
        () => {},
        error => {
          this.errorMessage =
            error.response?.data?.message || "Failed to resend mail.";
        },
        () => {
          this.isLoading = false;
        },
      );
    },
  },
});
